"use client";

import { useCurrency } from '@/contexts/CurrencyContext';
import React from 'react';

interface CurrencyProps {
  priceUSD: number;
  className?: string;
  showCurrencyCode?: boolean;
  variant?: 'default' | 'large' | 'small';
}

export const Currency: React.FC<CurrencyProps> = ({ 
  priceUSD, 
  className = '', 
  showCurrencyCode = false,
  variant = 'default'
}) => {
  const { formatPrice, selectedCurrency, loading } = useCurrency();

  if (loading) {
    return (
      <span className={`animate-pulse bg-gray-200 rounded ${className}`}>
        Loading...
      </span>
    );
  }

  if (!priceUSD || priceUSD <= 0) {
    return <span className={className}>—</span>;
  }

  const formattedPrice = formatPrice(priceUSD);

  const variantClasses = {
    default: 'text-base',
    large: 'text-lg font-semibold',
    small: 'text-sm',
  };

  return (
    <span className={`${variantClasses[variant]} ${className}`}>
      {formattedPrice}
      {showCurrencyCode && ` ${selectedCurrency}`}
    </span>
  );
};

// Currency Selector Component
interface CurrencySelectorProps {
  className?: string;
}

export const CurrencySelector: React.FC<CurrencySelectorProps> = ({ 
  className = '' 
}) => {
  const { 
    selectedCurrency, 
    setSelectedCurrency, 
    availableCurrencies, 
    loading 
  } = useCurrency();

  if (loading) {
    return (
      <div className={`animate-pulse bg-gray-200 rounded px-3 py-2 ${className}`}>
        Loading...
      </div>
    );
  }

  const popularCurrencies = ['USD', 'EUR', 'GBP', 'JPY', 'CAD', 'AUD'];
  const displayCurrencies = popularCurrencies.filter(currency => 
    availableCurrencies.includes(currency)
  );

  return (
    <select
      value={selectedCurrency}
      onChange={(e) => setSelectedCurrency(e.target.value)}
      className={`
        border border-gray-300 rounded-md px-3 py-2 bg-white text-sm
        focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
        ${className}
      `}
    >
      {displayCurrencies.map((currency) => (
        <option key={currency} value={currency}>
          {currency}
        </option>
      ))}
    </select>
  );
};

// Price Range Component (useful for filtering)
interface PriceRangeProps {
  minPriceUSD: number;
  maxPriceUSD: number;
  separator?: string;
  className?: string;
}

export const PriceRange: React.FC<PriceRangeProps> = ({
  minPriceUSD,
  maxPriceUSD,
  separator = ' - ',
  className = '',
}) => {
  return (
    <span className={className}>
      <Currency priceUSD={minPriceUSD} />
      {separator}
      <Currency priceUSD={maxPriceUSD} />
    </span>
  );
};

// Price Comparison Component (useful for showing discounts)
interface PriceComparisonProps {
  originalPriceUSD: number;
  currentPriceUSD: number;
  className?: string;
  showDiscount?: boolean;
}

export const PriceComparison: React.FC<PriceComparisonProps> = ({
  originalPriceUSD,
  currentPriceUSD,
  className = '',
  showDiscount = true,
}) => {
  const discountPercentage = Math.round(
    ((originalPriceUSD - currentPriceUSD) / originalPriceUSD) * 100
  );

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <Currency priceUSD={currentPriceUSD} variant="large" />
      {originalPriceUSD > currentPriceUSD && (
        <>
          <Currency 
            priceUSD={originalPriceUSD} 
            className="text-gray-500 line-through text-sm" 
          />
          {showDiscount && (
            <span className="bg-red-100 text-red-600 px-2 py-1 rounded text-xs font-medium">
              -{discountPercentage}%
            </span>
          )}
        </>
      )}
    </div>
  );
};
